import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Eye, Plus, Search } from 'lucide-react'
import { toast } from 'sonner'

import { useQueryClient } from '@tanstack/react-query'
import type { Role, UserWithRoles } from '@/lib/api/user-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { CreateUserDialog } from '@/components/users/create-user-dialog'
import { ChangePasswordDialog } from '@/components/users/change-password-dialog'
import { useRoles, useUsers, userKeys } from '@/hooks/use-user-query'
import { useAuth } from '@/hooks/use-auth'
import { userApi } from '@/lib/api/user-api'
import { impersonationApi } from '@/lib/api'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/_sidebar/users/')({
    component: UsersPage,
})

function UsersPage() {
    const [search, setSearch] = useState('')
    const [roleFilter, setRoleFilter] = useState('')
    const [page, setPage] = useState(1)
    const [dialogOpen, setDialogOpen] = useState(false)

    const { data: roles = [] } = useRoles()
    const { data, isLoading } = useUsers({
        search: search || undefined,
        role_id: roleFilter || undefined,
        page,
        per_page: 20,
    })

    const users = data?.data ?? []
    const meta = data?.meta

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">{m.user_title()}</h1>
                <Button size="sm" onClick={() => setDialogOpen(true)}>
                    <Plus className="size-4" />
                    {m.user_add()}
                </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
                    <Input
                        placeholder={m.user_search_placeholder()}
                        className="pl-9"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value)
                            setPage(1)
                        }}
                    />
                </div>
                <Select
                    value={roleFilter || 'all'}
                    onValueChange={(v) => {
                        setRoleFilter(v === 'all' ? '' : v)
                        setPage(1)
                    }}
                >
                    <SelectTrigger className="w-48">
                        <SelectValue placeholder={m.user_all_roles()} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.user_all_roles()}
                        </SelectItem>
                        {roles.map((r: Role) => (
                            <SelectItem key={r.id} value={r.id}>
                                {r.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.user_full_name()}</TableHead>
                            <TableHead>{m.auth_username()}</TableHead>
                            <TableHead>{m.user_role()}</TableHead>
                            <TableHead>{m.user_status()}</TableHead>
                            <TableHead>{m.user_created_date()}</TableHead>
                            <TableHead className="w-[100px]">
                                {m.common_actions()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : users.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {m.user_no_data()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.map((u: UserWithRoles) => (
                                <UserRow key={u.id} user={u} roles={roles} />
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            {meta && meta.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                        {m.user_total_count({ count: String(meta.total) })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page === 1}
                            onClick={() => setPage((p) => p - 1)}
                        >
                            {m.common_pagination_prev()}
                        </Button>
                        <span className="flex items-center px-2">
                            {page} / {meta.total_pages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= meta.total_pages}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            {m.common_pagination_next()}
                        </Button>
                    </div>
                </div>
            )}

            <CreateUserDialog open={dialogOpen} onOpenChange={setDialogOpen} />
        </div>
    )
}

function UserRow({ user, roles }: { user: UserWithRoles; roles: Role[] }) {
    const qc = useQueryClient()
    const { hasPermission, user: currentUser } = useAuth()
    const [pwDialogOpen, setPwDialogOpen] = useState(false)
    const [impersonating, setImpersonating] = useState(false)

    // Backend is the real gate; this only decides whether to show the entry.
    const canImpersonate =
        hasPermission('users.impersonate') && currentUser?.id !== user.id

    const handleImpersonate = async () => {
        setImpersonating(true)
        try {
            await impersonationApi.start(user.id)
            // React Query caches are endpoint-keyed, not identity-keyed: clear
            // everything and hard-reload so the app re-renders fully as the target.
            qc.clear()
            window.location.reload()
        } catch {
            setImpersonating(false)
            toast.error(m.impersonation_start_error())
        }
    }

    const toggleActive = async () => {
        try {
            await userApi.update(user.id, { is_active: !user.is_active })
            void qc.invalidateQueries({ queryKey: userKeys.lists() })
            toast.success(
                user.is_active
                    ? m.user_deactivate_success()
                    : m.user_activate_success(),
            )
        } catch {
            toast.error(m.user_update_error())
        }
    }

    const handleRoleChange = async (roleId: string) => {
        const currentRoleIds = (user.roles ?? []).map((r) => r.id)
        const newRoleIds = currentRoleIds.includes(roleId)
            ? currentRoleIds.filter((id) => id !== roleId)
            : [...currentRoleIds, roleId]
        try {
            await userApi.assignRoles(user.id, newRoleIds)
            void qc.invalidateQueries({ queryKey: userKeys.lists() })
            toast.success(m.toast_user_role_success())
        } catch {
            toast.error(m.toast_user_role_error())
        }
    }

    return (
        <TableRow>
            <TableCell className="font-medium">{user.full_name}</TableCell>
            <TableCell className="text-muted-foreground">
                {user.username}
            </TableCell>
            <TableCell>
                <div className="flex flex-wrap gap-1">
                    {roles.map((role) => {
                        const hasRole = user.roles?.some(
                            (r) => r.id === role.id,
                        )
                        return (
                            <button
                                key={role.id}
                                type="button"
                                onClick={() => handleRoleChange(role.id)}
                                title={
                                    hasRole
                                        ? m.user_role_remove_title({
                                              name: role.name,
                                          })
                                        : m.user_role_add_title({
                                              name: role.name,
                                          })
                                }
                            >
                                <Badge
                                    variant={hasRole ? 'default' : 'outline'}
                                    className="cursor-pointer text-xs"
                                >
                                    {role.name}
                                </Badge>
                            </button>
                        )
                    })}
                </div>
            </TableCell>
            <TableCell>
                <button type="button" onClick={toggleActive}>
                    <Badge
                        variant={user.is_active ? 'success' : 'secondary'}
                        className="cursor-pointer"
                    >
                        {user.is_active
                            ? m.customer_active()
                            : m.customer_inactive()}
                    </Badge>
                </button>
            </TableCell>
            <TableCell className="text-muted-foreground text-sm">
                {new Date(user.created_at).toLocaleDateString('vi-VN')}
            </TableCell>
            <TableCell>
                {canImpersonate && (
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleImpersonate}
                        disabled={impersonating}
                        className="text-xs"
                    >
                        <Eye className="size-4" />
                        {m.impersonation_view_as_action()}
                    </Button>
                )}
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setPwDialogOpen(true)}
                    className="text-xs"
                >
                    {m.user_change_pw_btn()}
                </Button>
                <ChangePasswordDialog
                    open={pwDialogOpen}
                    onOpenChange={setPwDialogOpen}
                    userId={user.id}
                    userName={user.full_name}
                />
            </TableCell>
        </TableRow>
    )
}
